import { atom } from "jotai";
import {
  isBlankCanvasAtom,
  isDraftModeIncludingTemplatesAtom,
  nodesAtom,
} from "@zapier/canvas-state";
import { aiPromptAtom } from "@zapier/streaming";
import { BuildModeSuggestionType } from "@zapier/copilot-tools";

// Generate dynamic initial messages based on URL prompt parameter
export const initialMessagesAtom = atom((get) => {
  const prompt = get(aiPromptAtom);
  const isDraftModeIncludingTemplates = get(isDraftModeIncludingTemplatesAtom);
  const isBlankCanvas = get(isBlankCanvasAtom);
  const nodes = get(nodesAtom);

  // If there's a prompt from the URL, create a user message with that prompt
  if (prompt && typeof prompt === "string" && prompt.trim()) {
    const decodedPrompt = decodeURIComponent(prompt.trim());
    return [
      {
        id: "canvas-welcome",
        role: "assistant" as const,
        content: decodedPrompt,
      },
    ];
  }

  // Show 3 default prompts for blank/new canvas (regardless of draft mode)
  if (isBlankCanvas) {
    return [
      {
        id: "canvas-welcome",
        role: "assistant" as const,
        content:
          "I'm here to help you build and edit zapier workflows on your canvas. I can help you create systems using zaps, tables, interfaces, and chatbots. I can also edit existing components and work with Zaps.\n\nHere are some example systems I can build for you:",
      },
      {
        id: "canvas-show-build-suggestions",
        role: "assistant" as const,
        content: "",
        toolInvocations: [
          {
            toolCallId: "show-build-suggestions-initial",
            toolName: "show_build_mode_suggestions",
            args: {
              suggestions: [
                BuildModeSuggestionType.LEAD_GENERATION,
                BuildModeSuggestionType.CUSTOMER_SUPPORT,
                BuildModeSuggestionType.ECOMMERCE_ORDER,
              ],
            },
            state: "result" as const,
            result: {
              success: true,
              message: "Showing build mode suggestions",
            },
          },
        ],
      },
    ];
  }

  // Show contextual suggestions for canvas with content but not in draft mode
  if (!isDraftModeIncludingTemplates) {
    // Check if there are any zap nodes on the canvas
    const hasZapNodes = nodes.some((node) => node.type === "zap");

    // Base suggestions for any non-blank canvas
    const suggestions = [
      BuildModeSuggestionType.TOUR_MY_CANVAS,
      BuildModeSuggestionType.EXPLAIN_MY_CANVAS,
    ];

    // Add Setup My Zaps only if there are actual zap nodes
    if (hasZapNodes) {
      suggestions.push(BuildModeSuggestionType.SETUP_ZAPS);
    }

    return [
      {
        id: "canvas-welcome-build-mode",
        role: "assistant" as const,
        content:
          "I'm here to help you build and edit zapier workflows on your canvas. I can help you create systems using zaps, tables, interfaces, and chatbots. I can also edit existing components and work with Zaps.\n\nHere are some quick actions to get started:",
      },
      {
        id: "canvas-show-build-suggestions",
        role: "assistant" as const,
        content: "",
        toolInvocations: [
          {
            toolCallId: "show-build-suggestions-initial",
            toolName: "show_build_mode_suggestions",
            args: {
              suggestions,
            },
            state: "result" as const,
            result: {
              success: true,
              message: "Showing build mode suggestions",
            },
          },
        ],
      },
    ];
  }

  // Default welcome message when no prompt is provided
  return [
    {
      id: "canvas-welcome",
      role: "assistant" as const,
      content:
        "I'm here to help you build and edit zapier workflows on your canvas. I can help you create systems using zaps, tables, interfaces, and chatbots. I can also edit existing components and work with Zaps.\n\nWhat would you like to do?",
    },
  ];
});
initialMessagesAtom.debugLabel = "initialMessagesAtom";
